import React, { ChangeEvent, Component, ReactElement } from "react";
import { FormFieldProps, readValue, writeValue } from "./FormField";
import { I8N, LocaleUtil } from "@eres/core";
import Strings from "@my-dld/assets/strings";
import { getSelectedPickerItem } from "@my-dld/utils/UIUtils";
import { SpinnerLoader } from "../SpinnerLoader";

export interface Props<T> extends FormFieldProps<T | undefined> {
	isLoading?: boolean;
	formatter?: PickerItemFormatter<T>;
	inputClassName?: string;
	errorInputClass?: string;
}

interface ReqiredProps<T> {
	items: T[];
	toIdentifier: ( item: T ) => string | undefined;
}

interface State {
	isFocus: boolean;
}

type XProps<T> = Props<T> & ReqiredProps<T>

class PickerInput<T> extends Component<XProps<T>, State> {
	static defaultProps: Partial<XProps<any>> = {
		formatter: item => LocaleUtil.getLocalValue( item ),
		items: [],
		containerClassName: "form-group field-style select-control",
		inputClassName: "form-select",
		errorInputClass: "form-input-error is-invalid",
		errorClassName: "invalid-feedback invalid-feedback-x",
	};

	constructor( props: XProps<T> ) {
		super( props );
		this.state = {
			isFocus: false,
		};
	}

	private static getPickerInputStyle( isFocus: boolean, isRequired?: boolean, value?: any, error?: string | ReactElement ) {
		if ( isFocus ) {
			return " focus-validation";
		} else if ( isRequired && ( value == undefined || error ) ) {
			return " empty-validation";
		} else if ( value !== undefined ) {
			return " valid-validation";
		} else if ( error ) {
			return " empty-validation";
		}
		return "";
	}

	onChange = ( event: ChangeEvent<HTMLSelectElement> ) => {
		let m = getSelectedPickerItem( event );
		let id = m?.value;
		if ( this.props.items && id ) {
			const v = this.props.items.find( item => this.props.toIdentifier( item ) === id );
			if ( v ) {
				if ( this.props.onValueChange )
					this.props.onValueChange( v );
				writeValue( this.props, v );
			}
		} else if ( id === "" ) {
			const v = undefined;
			if ( this.props.onValueChange )
				this.props.onValueChange( v );
			writeValue( this.props, v );
		}
	};

	render() {
		const {
			id, containerClassName, inputClassName, errorInputClass, labelClassName, label, disabled,
			required, error, errorClassName, items, formatter, toIdentifier, isLoading,
		} = this.props;
		let v = readValue( this.props );
		let inputStyle = PickerInput.getPickerInputStyle( this.state.isFocus, required, v, error );
		const inpProps = {
			id: id,
			className: inputClassName + inputStyle + ( error ? ` ${ errorInputClass }` : "" ),
			onChange: this.onChange,
			value: v ? toIdentifier( v ) : "",
			disabled: disabled,
			onFocus: this.onFocus,
			onBlur: this.onBlur,
		};
		return (
			<React.Fragment>
				<div className={ containerClassName }>
					{ label && <label className={ labelClassName } htmlFor={ id }><span>{ label }</span>
						{ required && <span className="asterisk">*</span> }

					</label>
					}
					{ isLoading && <span>
                            <span
	                            className={ "clearfix" }
	                            style={ { float: I8N.isRTL() ? "left" : "right" } }><SpinnerLoader
	                            extraClass={ "spinner-border-sm" }/></span>
                        </span>
					}
					<select { ...inpProps } >
						<option value={ "" }>{ Strings.please_select }</option>
						{ items && items.map( ( item ) => {
							let title = formatter ? formatter( item ) : item + "";
							let id = toIdentifier( item );
							return <option key={ "" + id } value={ "" + id }>{ title }</option>;
						} ) }
					</select>
					{ this.props.children }
					{ error && <div className={ errorClassName }>{ error }</div> }
				</div>
			</React.Fragment>
		);
	}

	private onFocus = () => {
		this.setState( { isFocus: true } );
	};

	private onBlur = () => {
		this.setState( { isFocus: false } );
	};
}

export interface PickerItemFormatter<T> {
	( item: T ): string;
}

export default PickerInput;
